import { FC, useEffect } from 'react';
import { Dropdown } from 'alurkerja-ui';
import { PanelLeft } from 'lucide-react';
import { TbBell } from 'react-icons/tb';
import TenantList from './TenantList';
import useAuthState from '@/states/auth.state';
import useTenantSettingState from '@/states/tenant-setting.state';
import { useUnifiedUserInfo, useUserAvatarUrl } from '@/utils/auth/userInfo';
import clsx from 'clsx';
import Avatar from '@/components/ui/Avatar';

export interface BellButtonProps {
  notificationCount?: number;
}

export interface HeaderProps extends BellButtonProps {
  showNotification?: boolean;
  onClickNotification?: () => void;
  onListenNotification?: (open: boolean) => void;
  avatarContent?: JSX.Element;
  notificationContent?: JSX.Element;
  toggled: boolean;
  setToggled: (toggled: boolean) => void;
  rightContent?: JSX.Element;
  breadcrumbs?: JSX.Element;
}

export const Header: FC<HeaderProps> = ({
  showNotification = true,
  onClickNotification,
  onListenNotification,
  avatarContent,
  notificationContent,
  notificationCount,
  toggled,
  setToggled,
  rightContent,
  breadcrumbs,
}) => {
  const { currentTheme } = useTenantSettingState();
  const { getValidToken } = useAuthState();
  const userInfo = useUnifiedUserInfo();
  const avatarUrl = useUserAvatarUrl();

  // Set up token refresh interval
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const token = await getValidToken();
        if (!token) {
          console.warn('Token is invalid or expired');
        }
      } catch (error) {
        console.error('Error checking token validity:', error);
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [getValidToken]);

  const buildNotification = (): JSX.Element => {
    if (!showNotification) {
      return <></>;
    }

    if (!onClickNotification) {
      return (
        <Dropdown
          triggerElement={<BellButton {...{ notificationCount }} />}
          content={notificationContent}
          onListen={onListenNotification}
        />
      );
    }

    return (
      <button onClick={onClickNotification} className="hover:cursor-pointer">
        <BellButton {...{ notificationCount }} />
      </button>
    );
  };


  return (
    <header className={clsx('grid grid-cols-[200px_1fr_auto] items-center w-full h-12 md:px-6 px-4 sticky top-0 z-10', currentTheme.headerClass)}>
      <div className="flex items-center gap-3 h-full">
        <TenantList className={currentTheme.headerContentClass} />

        <button
          onClick={() => setToggled(!toggled)}
          className="p-1 hover:bg-gray-100 md:hidden rounded-md transition-all duration-300"
        >
          <div className={clsx('transition-transform', toggled && 'rotate-180')}>
            <PanelLeft size={18} />
          </div>
        </button>
      </div>

      <div className="relative h-full w-full hidden md:block">
        <div className={clsx('absolute py-3 text-sm', toggled ? 'left-[10px]' : 'left-[20px]', currentTheme.headerContentClass)}>
          {breadcrumbs}
        </div>
      </div>

      <div className={clsx('flex items-center justify-end gap-2', currentTheme.headerContentClass)}>
        {rightContent}
        {buildNotification()}
        <Dropdown
          triggerElement={
            <div className={clsx('flex items-center justify-center gap-2 cursor-pointer', currentTheme.headerContentClass)}>
              <Avatar name={userInfo?.name ?? ''} imagePath={avatarUrl} size="lg" />
              <div className="hidden md:block text-right">
                <span className="font-medium text-sm block">{userInfo?.name}</span>
                {userInfo?.email && (
                  <span className="text-xs opacity-60 block leading-tight">{userInfo.email}</span>
                )}
              </div>
            </div>
          }
          content={avatarContent}
        />
      </div>
    </header>
  );
};

const BellButton: FC<BellButtonProps> = ({ notificationCount }) => {
  const count = notificationCount ?? 0;
  let rightClass = 'right-0';
  if (count >= 100) {
    rightClass = '-right-4';
  } else if (count >= 10) {
    rightClass = '-right-2';
  }

  return (
    <div className="relative p-1 hover:cursor-pointer hover:bg-black/5 rounded-md">
      {count > 0 && (
        <div
          className={`absolute top-0 py-[0.2px] px-[4px] text-[10px] text-white bg-red-500 rounded-full ${rightClass}`}
        >
          {count >= 100 ? '99+' : count}
        </div>
      )}

      <TbBell size={18} className="-rotate-12" />
    </div>
  );
};

export default Header;
